import { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Document, Page, pdfjs } from 'react-pdf';
import { makeStyles, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { ellipsis } from '../../../constants/helperCssRules';
import Candidate from '../../shared/candidatesList/candidate';
import ActionButton from '../../shared/actionButton';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import {
  clearCandidate,
  getCandidate,
} from '../../../store/modules/candidateDetails';
import { candidateDetailsSelector } from '../../../store/selectors/candidateDetailsSelector';
import CustomFormDrawer from '../../shared/customFormDrawer';
import PageContainer from '../../shared/pageContainer';
import { useTranslations } from '../../../utility/useTranslations';
import { APP_PAGES } from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { api_candidate } from '../../../constants/apiRoutes';
import http, { API_DOMAIN, API_PROTOCOL } from '../../../utility/http';
import { isArray, isArrayEmpty, isObject } from '../../../utility/helpers';
import { getSubdomain, showSuccessMessage } from '../../../utility/uiUtils';
import { hasAuthPermissionsCheck } from '../../../utility/permissions';
import AlertDialog from '../../shared/alertDialog';
import { ReactComponent as ZoomIcon } from '../../../assets/icons/_button_new.svg';
import {
  CONTACT_DETAILS_INFO,
  getCandidateFields,
  prepareCandidateInitialData,
} from '../jobsPage/config';
import { CANDIDATE_PROFILE_INFO, getPeriod } from './config';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import { getAddress } from '../../shared/candidateInfo/config';
import NotificationCard from '../../shared/notificationCard';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  form: {
    display: 'grid',
    gridColumnGap: 24,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridTemplateAreas: `
          "firstName lastName"
          "professionTitle email"
          "dateOfBirth phone"
          "referral ."
          "cv parseCheckbox"
          "education education"
          "experience experience"
          "skills skills"
          "city address"
          "country ."
        `,
  },
  firstName: {
    gridArea: 'firstName',
  },
  lastName: {
    gridArea: 'lastName',
  },
  email: {
    gridArea: 'email',
  },
  cv: {
    gridArea: 'cv',
  },
  parseCheckbox: {
    gridArea: 'parseCheckbox',
    display: 'flex',
    alignItems: 'center',
  },
  experience: {
    gridArea: 'experience',
  },
  skills: {
    gridArea: 'skills',
  },
  professionTitle: {
    gridArea: 'professionTitle',
  },
  education: {
    gridArea: 'education',
  },
  referral: {
    gridArea: 'referral',
  },
  dateOfBirth: {
    gridArea: 'dateOfBirth',
  },
  phone: {
    gridArea: 'phone',
  },
  address: {
    gridArea: 'address',
  },
  city: {
    gridArea: 'city',
  },
  country: {
    gridArea: 'country',
  },
  root: {
    display: 'grid',
  },
  candidate: {
    marginTop: spacing(4),
  },
  icon: {
    minWidth: 'max-content',
    marginRight: spacing(2),
  },
  infoWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  info: {
    marginTop: spacing(5),
    marginBottom: spacing(13),
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 260px) minmax(0, 1fr) minmax(0, 1fr)',
    gridColumnGap: 16,
  },
  sectionWrap: {
    paddingBottom: spacing(5.5),
    paddingTop: spacing(5.5),
    borderBottom: `1px solid ${primary.bluish7}`,
  },
  cvSectionWrap: {
    height: 241,
  },
  sectionField: {
    marginBottom: spacing(4.75),
    color: primary.bluish2,
    lineHeight: '18px',
    textTransform: 'uppercase',
  },
  sectionData: {
    ...ellipsis(),
    maxWidth: 330,
  },
  timelineWrapper: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 260px) minmax(0, 1fr)',
    gridColumnGap: 16,
    marginBottom: spacing(4),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  addressWrapper: {
    marginBottom: spacing(4),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  dateField: {
    color: primary.bluish2,
    lineHeight: '18px',
  },
  fieldWrapper: {
    maxWidth: 'inherit',
  },
  field: {
    color: primary.bluish2,
  },
  scale: {
    zIndex: 99,
    position: 'relative',
    left: 45,
    top: 82,
  },
  document: {
    display: 'flex',
    top: -44,
    position: 'relative',
  },
  pdfPage: {
    justifyContent: 'center',
    display: 'flex',
    width: 'fit-content',
    '& > canvas': {
      border: `1px solid ${primary.bluish7}`,
      width: '143px !important',
      height: '202px !important',
    },
  },
  uploadNotification: {
    backgroundColor: primary.green5,
    padding: spacing(2, 0, 2, 0),
    marginBottom: spacing(1.75),
    marginTop: spacing(1.75),
  },
}));

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const CandidateDetailsPage = ({
  dispatch,
  navigate,
  location,
  auth,
  accessControl,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.CANDIDATE_DETAILS);
  const [canEdit, canDelete] = hasAuthPermissionsCheck(
    [PERMISSION_CODES.RECRUITMENT_EDIT, PERMISSION_CODES.RECRUITMENT_DELETE],
    accessControl
  );
  const { candidateId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteCandidateDialogOpened, setIsDeleteCandidateDialogOpened] =
    useState(false);

  const [isEditCandidateDrawerOpened, setIsEditCandidateDrawerOpened] =
    useState(false);
  const { candidate } = useSelector(candidateDetailsSelector);

  const candidateInitialData = prepareCandidateInitialData(candidate);
  const getCandidateData = useCallback(async () => {
    await Promise.all([getCandidate(dispatch, candidateId)]);
    setIsLoading(false);
  }, [dispatch, candidateId]);

  useEffect(() => {
    getCandidateData();

    return () => {
      dispatch(clearCandidate());
    };
  }, [dispatch, getCandidateData]);

  const handleEditDrawer = () => {
    setIsEditCandidateDrawerOpened(true);
  };

  const handleDeleteCandidate = () => {
    setIsDeleteCandidateDialogOpened(true);
  };

  const handleCloseDelete = () => {
    setIsDeleteCandidateDialogOpened(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await http.delete(api_candidate(candidateId));
      showSuccessMessage(translations.deleteCandidateDialog.deleteSuccess);
    } finally {
      setIsDeleteCandidateDialogOpened(false);
    }
  };

  const handleSaveCandidate = async person => {
    const { cv, ...candidateData } = person;
    if (isObject(cv) || !cv) {
      const { parse_cv_data, ...data } = candidateData;
      const formData = new FormData();
      formData.append('cv', cv || '');
      formData.append('parse_cv_data', parse_cv_data || false);

      await http.patch(api_candidate(candidate.id), data).then(() => {
        http.patch(api_candidate(candidate.id), formData).then(() => {
          showSuccessMessage(translations.successMessages.candidateSaved);
          setIsEditCandidateDrawerOpened(false);
          getCandidateData();
        });
      });
    } else {
      await http.patch(api_candidate(candidate.id), candidateData).then(() => {
        showSuccessMessage(translations.successMessages.candidateSaved);
        setIsEditCandidateDrawerOpened(false);
        getCandidateData();
      });
    }
  };

  const handleCloseEditCandidatesDrawer = () => {
    setIsEditCandidateDrawerOpened(false);
  };

  const subdomain = getSubdomain(window.location.hostname);

  const getFileUrl = () => {
    if (candidate.cv && candidate.cv.includes('http')) return candidate.cv;
    if (subdomain && subdomain.length > 0) {
      return `${API_PROTOCOL}://${subdomain}.api.${API_DOMAIN}${candidate.cv}`;
    }
    return null;
  };

  const renderEditButton = () => {
    return (
      <ActionButton
        type={ACTION_BUTTON_TYPES.EDIT}
        onClickHandler={handleEditDrawer}
        tooltipText={translations.edit}
      />
    );
  };

  const renderFieldList = fieldKey => {
    switch (fieldKey) {
      case CANDIDATE_PROFILE_INFO.EDUCATION: {
        return isArrayEmpty(candidate[fieldKey]) ? (
          <Typography
            variant="subtitle1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        ) : (
          <>
            {candidate[fieldKey].map((userInfo, index) => {
              const education = userInfo.degree
                ? `${userInfo.school}, ${userInfo.degree}`
                : userInfo.school;

              return (
                <div
                  key={`education_${index}`}
                  className={classes.timelineWrapper}
                >
                  <TextBoxWithTooltip
                    variant="body1"
                    className={classes.dateField}
                    component="div"
                    text={getPeriod(userInfo)}
                  />

                  <div className={classes.fieldWrapper}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      className={classes.field}
                    >
                      {education}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </>
        );
      }
      case CANDIDATE_PROFILE_INFO.EXPERIENCE: {
        return isArrayEmpty(candidate[fieldKey]) ? (
          <Typography
            variant="subtitle1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        ) : (
          <>
            {candidate[fieldKey].map((userInfo, index) => {
              return (
                <div
                  key={`experience_${index}`}
                  className={classes.timelineWrapper}
                >
                  <TextBoxWithTooltip
                    variant="body1"
                    className={classes.dateField}
                    component="div"
                    text={getPeriod(userInfo)}
                  />

                  <div className={classes.fieldWrapper}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      className={classes.field}
                    >
                      {`${userInfo.company}, ${userInfo.job_title}`}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </>
        );
      }
      case CANDIDATE_PROFILE_INFO.SKILLS: {
        return isArrayEmpty(candidate[fieldKey]) ? (
          <Typography
            variant="subtitle1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        ) : (
          <div className={classes.fieldWrapper}>
            <Typography
              variant="body1"
              className={classes.field}
              component="div"
            >
              {candidate[fieldKey].map(skill => skill.name).join(', ')}
            </Typography>
          </div>
        );
      }
      case CANDIDATE_PROFILE_INFO.CV: {
        const fileUrl = getFileUrl();
        return candidate.cv && fileUrl ? (
          <>
            <div className={classes.scale}>
              <Link to={candidate.cv} target="_blank">
                <ZoomIcon />
              </Link>
            </div>
            <div className={classes.document}>
              <Document file={fileUrl} onContextMenu={e => e.preventDefault()}>
                <Page pageNumber={1} className={classes.pdfPage} />
              </Document>
            </div>
          </>
        ) : (
          <Typography
            variant="body1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        );
      }
      case CANDIDATE_PROFILE_INFO.CONTACT_DETAILS: {
        return (
          <>
            {Object.values(CONTACT_DETAILS_INFO).map((value, index) => {
              switch (value) {
                case CONTACT_DETAILS_INFO.ADDRESS: {
                  return (
                    <div
                      key={`contact_info_${index}`}
                      className={classes.addressWrapper}
                    >
                      <TextBoxWithTooltip
                        variant="body1"
                        className={classes.field}
                        component="div"
                        text={`${
                          translations.candidateInfoFields[value]
                        }: ${getAddress(candidate)}`}
                      />
                    </div>
                  );
                }
                default: {
                  return (
                    <div
                      key={`contact_info_${index}`}
                      className={classes.addressWrapper}
                    >
                      <TextBoxWithTooltip
                        variant="body1"
                        className={classes.field}
                        component="div"
                        text={`${translations.candidateInfoFields[value]}: ${
                          candidate[value] ? candidate[value] : '-'
                        }`}
                      />
                    </div>
                  );
                }
              }
            })}
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    !isLoading && (
      <PageContainer
        {...rest}
        navigate={navigate}
        translations={translations}
        location={location}
        auth={auth}
        accessControl={accessControl}
        shouldPassProps={false}
      >
        <div className={classNames(classes.root)}>
          <Candidate
            className={classes.candidate}
            user={candidate}
            label={candidate.profession_title}
            hasEdit={canEdit}
            editButton={renderEditButton}
            hasLabel
          />
          <NotificationCard
            className={classes.uploadNotification}
            shouldFade={candidate?.ongoing_cv_parsing}
            content={translations.uploadCvMessage}
          />
          {Object.values(CANDIDATE_PROFILE_INFO).map(fieldKey => {
            const isCvField = CANDIDATE_PROFILE_INFO.CV !== fieldKey;
            return (
              <div
                key={fieldKey}
                className={classNames(classes.sectionWrap, {
                  [classes.cvSectionWrap]: !isCvField && candidate.cv,
                })}
              >
                <Typography
                  variant="subtitle1"
                  className={classes.sectionField}
                >
                  {translations.candidateInfoFields[fieldKey]}
                </Typography>
                {isArray(candidate[fieldKey]) ||
                [
                  CANDIDATE_PROFILE_INFO.CV,
                  CANDIDATE_PROFILE_INFO.CONTACT_DETAILS,
                ].includes(fieldKey) ? (
                  renderFieldList(fieldKey)
                ) : (
                  <Typography
                    variant="body1"
                    component="div"
                    className={classes.sectionData}
                  >
                    {candidate[fieldKey] || '-'}
                  </Typography>
                )}
              </div>
            );
          })}
        </div>
        <CustomFormDrawer
          customFormClass={classes.form}
          translations={translations.addCandidatesManually}
          isOpened={isEditCandidateDrawerOpened}
          initialData={candidateInitialData}
          fields={getCandidateFields(classes)}
          onClose={handleCloseEditCandidatesDrawer}
          onSave={handleSaveCandidate}
          onDelete={handleDeleteCandidate}
          hideDelete={!canDelete}
          isInitialValid
          hasCancelButton
          isMiddleWidth
        />
        <AlertDialog
          translations={translations.deleteCandidateDialog}
          isOpened={isDeleteCandidateDialogOpened}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
          isWarning
        />
      </PageContainer>
    )
  );
};

CandidateDetailsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(CandidateDetailsPage);
